import { useQueryClient } from "@tanstack/react-query";
import {
  appRoles,
  isRealOrgId,
} from "@/src/constants/roles-permissions.constants";
import { Href, router, usePathname, useRootNavigationState } from "expo-router";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  Text,
  View,
} from "react-native";
import { useTranslation } from "react-i18next";

import { BlocksUser } from "@/src/models/user.model";
import { accountStore, StoredAccount } from "@/src/services/api/account-store";
import { clearSessionCache, switchOrg } from "@/src/services/blocks/session";
import { ssoLogout, startLogin } from "@/src/services/blocks/sso";
import { authEvents } from "@/src/services/api/auth-events";
import {
  clearTokens,
  decodeToken,
  discardUnrememberedSession,
  getRefreshToken,
  getSessionStatus,
  getToken,
  invalidateIdentity,
  setOrgSelected,
} from "@/src/services/api/auth.api";
import { useBlocksUserByIdQuery } from "@/src/services/users.blocks.api";
import { useNotificationConnection } from "../hooks/features/notification/use-notifications";
import {
  clearPreferredOrg,
  useGetPreferredOrgQuery,
} from "../services/profile/profile.hooks";
import { useGetUserGroupMemberships } from "../services/api/groups/groups.hooks";
import { useOrganizationsQuery } from "../services/uam/permissions.blocks.api";
import { getPrimaryRole } from "../utils/role.utils";
import {
  canCommitSession,
  isSessionResolved as isSessionResolvedFor,
  shouldRecheckSession,
  shouldRouteToLogin,
} from "../utils/session-state.utils";
import {
  isPreferredOrgValid,
  shouldAttemptPostLoginRedirect,
  shouldAutoSwitchToPreferredOrg,
} from "../utils/preferred-org.utils";
import {
  clearIntendedPath,
  consumeIntendedPath,
  isPreservablePath,
  rememberIntendedPath,
} from "../utils/deep-link.utils";
import Toast from "react-native-toast-message";

type JwtPayload = Readonly<{
  user_id: string;
  roles?: string | string[];
  email: string;
  org_id?: string;
  /**
   * The org the session cookie is actually scoped to (`/iam/me`'s
   * `organizationId`), as opposed to `org_id`, which is the org the app resolved
   * it *should* be in. They diverge whenever switch-org has not run yet, and
   * `roles` belongs to this one — see the reconcile note in `decodeToken`.
   */
  session_org_id?: string;
}>;

const toRoleArray = (roles?: string | string[] | null): string[] => {
  if (Array.isArray(roles))
    return roles.filter((r): r is string => typeof r === "string");
  if (typeof roles === "string")
    return roles
      .split(",")
      .map((r) => r.trim())
      .filter(Boolean);
  return [];
};

type AuthContextType = Readonly<{
  user: BlocksUser | null;
  orgId: string | undefined;
  /** Every role the user holds for the active org (union of JWT claim + membership). */
  roles: string[];
  userGroups: string[] | null;
  isAdmin: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * True once the session is settled enough for `roles` to be trustworthy: either
   * signed out, or signed in with an identity loaded. Permission gates must not
   * deny anything while this is false — an unresolved session has no roles, and
   * "no roles yet" is not "denied".
   */
  isSessionResolved: boolean;
  isSwitching: boolean;
  appRole: string | null;
  refreshAuth: (skipRedirect?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  isSuperAdmin: boolean;
  accounts: StoredAccount[];
  switchAccount: (userId: string) => Promise<void>;
  addAccount: () => void;
  currentOrgName: string | undefined;
}>;

const AuthContext = createContext<AuthContextType | null>(null);

const deriveRole = (
  user: BlocksUser | null,
  jwtPayload: JwtPayload | null,
  orgId: string | undefined,
  storedRole?: string,
): string | null => {
  const jwtRoles = appRoles(toRoleArray(jwtPayload?.roles));
  if (jwtRoles.length) return jwtRoles[0];

  const currentOrgMembership = user?.memberships?.find(
    (membership) => membership.organizationId === orgId,
  );
  const membershipRole = getPrimaryRole(currentOrgMembership?.roles);
  if (membershipRole) return membershipRole;

  // The user's org-independent roles. Reached whenever `memberships` is empty —
  // i.e. any session still scoped to `default` — which is precisely when the two
  // sources above have nothing to offer.
  const userRole = getPrimaryRole(user?.roles);
  if (userRole) return userRole;

  return storedRole ?? null;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [initialized, setInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [jwtPayload, setJwtPayload] = useState<JwtPayload | null>(null);
  const [storedRole, setStoredRole] = useState<string | undefined>(undefined);
  const [accounts, setAccounts] = useState<StoredAccount[]>([]);
  const [isSwitching, setIsSwitching] = useState(false);
  /**
   * "Is this session scoped to a real org yet?" — read from the SESSION, not from
   * the persisted `org_selected` flag.
   *
   * That flag lives in secureStorage and is cleared only on logout and on
   * password login (`use-login.ts`), never on the SSO path. So an SSO re-login
   * inherited last week's `true`, skipped the org picker, and went straight to
   * `/feeds` on a brand-new session still scoped to `default` — where switch-org
   * had never run and the user therefore held no app roles at all. The flag
   * describes a server-side property of the session, so it cannot be cached
   * across sessions; `session_org_id` is that property.
   */
  const [orgSelected, setOrgSelectedState] = useState(false);
  const hasRedirected = useRef(false);
  const wasAuthenticated = useRef(false);
  const rootNavReady = !!useRootNavigationState()?.key;
  // Reads the same global router store as `useRootNavigationState` above (a
  // `useSyncExternalStore`, not navigator-scoped context), so it is valid here
  // even though AuthProvider sits *outside* the <Slot /> navigator.
  const pathname = usePathname();
  // Live-updating mirror of `pathname` for the post-login redirect effect below:
  // that effect's closure captures `pathname` at the moment it fires (always "/"
  // there), so it cannot itself tell whether a `router.replace` landed. A ref
  // updated on every render can.
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // "Remember me" is enforced here, before anything reads the session.
        // The toggle used to be written to storage and never read again, so it
        // did nothing at all: a user who left it off stayed signed in forever.
        // On a fresh browser session / app run this revokes and clears a session
        // they asked not to be remembered — for BOTH the password and the SSO
        // way in, since both persist the same preference.
        if (await discardUnrememberedSession()) {
          clearSessionCache();
          setIsAuthenticated(false);
          return;
        }

        // v4: the session is an HttpOnly cookie, so there is nothing to rehydrate
        // from storage and no token to refresh into place on load. `decodeToken`
        // now hits /iam/v4/iam/me, which returns null when the cookie is absent
        // or expired — that single call answers both "signed in?" and "who?".
        //
        // `reconcileOrg` also brings the session's org scope in line with the org
        // the app resolved, because `/iam/me`'s `roles` are scoped to it — see
        // the long note in `decodeToken`. This is one of exactly two places that
        // establish a session, so it is one of exactly two callers that pass it.
        const decoded = await decodeToken({ reconcileOrg: true });
        const auth = !!decoded;
        setIsAuthenticated(auth);

        if (auth) {
          setOrgSelectedState(isRealOrgId(decoded.session_org_id));
          setJwtPayload(decoded);

          const role = appRoles(toRoleArray(decoded.roles))[0];
          if (role) setStoredRole(role);

          // The account list is now just a display/quick-switch hint — it no
          // longer carries tokens, because a cookie session cannot be stored or
          // swapped client-side (see `switchAccount`).
          if (decoded.user_id) {
            const existing = (await accountStore.getAll()).find(
              (a) => a.userId === decoded.user_id,
            );
            await accountStore.upsert({
              userId: decoded.user_id,
              email: decoded.email,
              accessToken: "",
              refreshToken: "",
              role: role ?? existing?.role,
              organization: decoded.org_id ?? existing?.organization,
            });
            await accountStore.setActive(decoded.user_id);
            if (!role && existing?.role) setStoredRole(existing.role);
          }

          setAccounts(await accountStore.getAll());
        }
      } catch {
        setIsAuthenticated(false);
      } finally {
        setInitialized(true);
      }
    };

    // A cold load is the only moment the app can still see the URL the visitor
    // arrived on. If that load is signed out, the root guard sends them to
    // /login and the destination is gone — so remember it here, before any
    // navigation, for the post-login landing to pick up.
    if (typeof window !== "undefined") {
      rememberIntendedPath(
        `${window.location.pathname}${window.location.search}`,
      );
    }

    initAuth();

    const unsubscribe = authEvents.onLogout(async () => {
      await clearTokens();
      clearSessionCache();
      setIsAuthenticated(false);
      setJwtPayload(null);
      setStoredRole(undefined);
      setAccounts([]);
      hasRedirected.current = false;
      setOrgSelectedState(false);
      router.replace("/(auth)/login");
    });

    return unsubscribe;
  }, []);

  const {
    data: blocksUser,
    isLoading,
    isError: isUserError,
    refetch: refetchUser,
  } = useBlocksUserByIdQuery(jwtPayload?.user_id ?? "", {
    enabled: isAuthenticated && !!jwtPayload?.user_id,
  });
  const user: BlocksUser | null = blocksUser ?? null;

  // Never surface the "default" bookkeeping org as the active org.
  const rawOrgId = jwtPayload?.org_id;
  const orgId = isRealOrgId(rawOrgId) ? rawOrgId : undefined;

  // Group membership is read from the `groupmembership` entity, not from the
  // user profile's `groupIds`. Nothing ever writes that profile field — joining
  // or creating a group only inserts a membership row — so it is null for every
  // user, which made `userGroups` always empty and silently hid every group post
  // from the main feed (`filterPostVisibility` in use-posts.ts). Memberships are
  // the source of truth the group screens already use.
  const { data: userGroupsData } = useGetUserGroupMemberships(
    orgId ? user?.itemId : undefined,
  );
  const userGroups = userGroupsData ?? null;

  // Fetched once after login when org hasn't been selected yet so the redirect
  // effect can auto-switch the user into their preferred org without showing the picker.
  const { data: preferredOrgId, isLoading: isPreferredOrgLoading } =
    useGetPreferredOrgQuery(jwtPayload?.user_id ?? "", {
      enabled: isAuthenticated && !!jwtPayload?.user_id && !orgSelected,
    });
  const appRole = deriveRole(user, jwtPayload, orgId, storedRole);
  // Tolerate casing/separator variants ("super-admin", "Super_Admin", ...).
  const normalize = (role?: string | null) =>
    (typeof role === "string" ? role : "").toLowerCase().replace(/[\s_-]/g, "");

  const currentOrgRoles =
    user?.memberships?.find((membership) => membership.organizationId === orgId)
      ?.roles ?? [];
  const { data: organizationsData, isLoading: isOrganizationsLoading } =
    useOrganizationsQuery({ enabled: isAuthenticated });

  /**
   * The user's real org memberships.
   *
   * This CANNOT come from `user.memberships`. That array is built from
   * `/iam/v4/iam/me`, which reports a single `organizationId` — the *session's*
   * org, not a list — and returns no `organizationIds` field at all (verified
   * live against the prod tenant 2026-08-13). A session that has not switched org
   * yet carries `organizationId: "default"`, so `user.memberships` is `[default]`
   * and, once default is filtered, empty — for a user who plainly belongs to an
   * org. That is what stranded prod on `/org-select?pending=true`.
   *
   * `GET /iam/v4/iam/organizations/my` is the only endpoint that answers "which
   * orgs does this user belong to" — it returned `Selise` for the very session
   * whose `/iam/me` said `default`.
   */
  const realOrgs = useMemo(
    () => (organizationsData ?? []).filter((org) => isRealOrgId(org.itemId)),
    [organizationsData],
  );

  // Membership-shaped view of `realOrgs`, for the preferred-org helpers. Roles
  // stay on `user.memberships`, which is correct once the session is scoped to a
  // real org — this only answers "is this org still one of mine".
  const realOrgMemberships = useMemo(
    () =>
      realOrgs.map((org) => ({
        organizationId: org.itemId,
        roles: [] as readonly string[],
        permissions: [] as readonly string[],
      })),
    [realOrgs],
  );
  const currentOrgName = organizationsData?.find(
    (org) => org.itemId === orgId,
  )?.name;

  // A user can hold multiple roles — the JWT `roles` claim becomes an array and
  // memberships carry a roles[]. Expose the de-duped union so permission checks
  // honor every role (e.g. "super-admin" + "member"), not just the first one.
  // `appRoles` drops `clouduser`: it exists so the account can call Blocks
  // platform endpoints, not to grant anything in this app. Leaving it in would
  // let a gate pass via a permission attached to the platform role.
  const roles = [
    ...new Set(
      appRoles([...toRoleArray(jwtPayload?.roles), ...currentOrgRoles]),
    ),
  ];
  const isSessionResolved = isSessionResolvedFor({
    initialized,
    authenticated: isAuthenticated,
    hasIdentity: !!jwtPayload,
    // `user` is `null`, not an error, when the IAM read is refused — so without
    // this the session read as "resolved with no roles" and every gate denied.
    hasUser: !!user,
    isUserLoading: isLoading,
  });

  const roleCandidates = roles.map(normalize);
  const isSuperAdmin = roleCandidates.includes("superadmin");
  const isAdmin = isSuperAdmin || roleCandidates.includes("admin");

  useEffect(() => {
    const currentOrgMembership = user?.memberships?.find(
      (membership) => membership.organizationId === orgId,
    );
    const membershipRole = getPrimaryRole(currentOrgMembership?.roles);
    if (membershipRole) {
      setStoredRole(membershipRole);
      if (jwtPayload?.user_id) {
        accountStore.getAll().then((all) => {
          const existing = all.find((a) => a.userId === jwtPayload.user_id);
          if (existing && existing.role !== membershipRole) {
            accountStore.upsert({ ...existing, role: membershipRole });
          }
        });
      }
    }
  }, [user, orgId]);

  useEffect(() => {
    // Wait for both the IAM user and the preferred-org profile query to settle
    // before deciding where to route. isPreferredOrgLoading is false when
    // orgSelected=true (query disabled) so it never blocks the returning-user path.
    // `rootNavReady` is load-bearing: expo-router silently DROPS a `router.replace`
    // issued before the root navigator has mounted. It does not throw, so the catch
    // below never fires — and `hasRedirected` was already true by then, so the
    // effect never retried. The result was a session that is authenticated but
    // stranded on the login screen (or on the spinner, e.g. straight after the SSO
    // callback) until a manual reload, intermittently, depending on whether the
    // navigator happened to mount within a tick. `rootNavReady` alone doesn't fully
    // close this: it reflects the ROOT navigator, not whichever nested one owns the
    // actual target route (e.g. `(tabs)`/`(auth)`), which can still mount later.
    // `replaceWhenLanded` below verifies the navigation actually took effect —
    // via `pathnameRef`, not the stale `pathname` this closure captured — and
    // retries instead of latching `hasRedirected` on a silently-dropped replace.
    // `!user` is repeated outside the predicate so TypeScript narrows `user` to
    // non-null for the rest of the effect; the predicate alone cannot express that.
    if (
      !user ||
      !shouldAttemptPostLoginRedirect({
        initialized,
        rootNavReady,
        isUserLoading: isLoading,
        isPreferredOrgLoading,
        isOrganizationsLoading,
        hasUser: !!user,
        hasRedirected: hasRedirected.current,
        // Password-reset / activation links carry a single-use `code` in the URL
        // and must not be replaced by /feeds. Note this deliberately does NOT set
        // `hasRedirected`, so leaving the screen (e.g. the router.replace to
        // /login after a successful reset) still lets the normal redirect run.
        pathname,
      })
    )
      return;

    hasRedirected.current = true;

    // Bounded retry (≈8 x 60ms ≈ 0.5s worst case) for the dropped-replace race
    // described above. `pathnameRef` is a live-updating ref (see its `useEffect`
    // near the top of this provider) so this can observe a landed navigation
    // from inside a closure that itself never re-renders.
    const replaceWhenLanded = async (
      href: Href,
      attempts = 8,
      delayMs = 60,
    ): Promise<boolean> => {
      // Compare against wherever we actually started, not a hardcoded "/" —
      // this effect can also fire from a deep-link path.
      const from = pathnameRef.current;
      for (let i = 0; i < attempts; i++) {
        router.replace(href);
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        if (pathnameRef.current !== from) return true;
      }
      return false;
    };

    const doRedirect = async () => {
      try {
        const hasName = Boolean(
          user.firstName?.trim() || user.lastName?.trim(),
        );

        // Where the user belongs once the account is ready:
        //
        // 1. a link they followed while signed out, remembered before the bounce
        //    to /login (see the mount effect above);
        // 2. otherwise the screen this load landed on, when it is an app screen
        //    rather than an entry screen — a reloaded or shared `/profile/<id>`,
        //    `/groups/<id>`, `/settings`, `/events` … stays put instead of being
        //    rewritten to the feed;
        // 3. otherwise the default tab.
        //
        // Onboarding still wins when it applies (a first-time user must set their
        // name before anything else). The stored intent is only consumed when the
        // current path cannot answer the question — on the login screen it cannot
        // — so an onboarding detour does not throw it away.
        //
        // Web only: a native cold start arrives through the linking config, where
        // `pathname` is a router path rather than a URL.
        const intended = isPreservablePath(pathname)
          ? null
          : consumeIntendedPath();
        const landedOn =
          intended ??
          (typeof window !== "undefined"
            ? `${pathname}${window.location.search}`
            : null);

        const landingRoute: Href = !hasName
          ? `/profile/${user.itemId}/edit?onboarding=true`
          : isPreservablePath(landedOn)
            ? (landedOn as Href)
            : "/feeds";

        /**
         * "Already there" counts as landed.
         *
         * A cold load of a profile-style link IS `landingRoute`: replacing it with
         * itself is a no-op, which `replaceWhenLanded` can only read as a dropped
         * navigation — and its caller then unlatches `hasRedirected`, so the next
         * dependency change (any tab switch) re-ran this effect against the NEW
         * path and could rewrite ordinary navigation to /feeds.
         */
        const goToLanding = async (): Promise<boolean> =>
          String(landingRoute).split(/[?#]/)[0].replace(/\/+$/, "") ===
          (pathnameRef.current ?? "")
            ? true
            : replaceWhenLanded(landingRoute);

        if (realOrgs.length === 0) {
          if (!(await replaceWhenLanded("/(auth)/org-select?pending=true"))) {
            hasRedirected.current = false;
          }
          return;
        }

        if (!orgSelected) {
          // CR-B1-04: auto-switch into the preferred org when it is still a valid
          // membership and differs from the JWT's current org_id.
          if (
            shouldAutoSwitchToPreferredOrg(
              preferredOrgId,
              orgId,
              realOrgMemberships,
            )
          ) {
            try {
              await switchOrg(preferredOrgId!);
              // Load-bearing, and previously missing: `switchOrg` rotates the
              // session cookie, and `/iam/me`'s `roles` are scoped to whichever
              // org it now carries. Without re-reading, the context kept the
              // PRE-switch payload — the `default`-scoped one with no app roles —
              // so the user landed in their org labelled "Unknown role" and
              // blocked by every permission gate. `org-select.tsx` calls
              // `refreshAuth` for exactly this reason on the manual path; the
              // auto-switch branch was written later and never got it.
              //
              // `skipRedirect: true` because we navigate ourselves two lines
              // below — re-arming `hasRedirected` here would race this effect.
              await refreshAuth(true);
              await setOrgSelected(true);
              setOrgSelectedState(true);
              if (!(await goToLanding())) {
                hasRedirected.current = false;
              }
              return;
            } catch (err: any) {
              // switchOrg failed — surface the server message and fall through to picker.
              Toast.show({
                type: "error",
                text1: err.message || "ERROR_SWITCH_ORG_FAILED",
              });
            }
          } else if (
            // FIX-02: preference is valid and already equals the current org —
            // treated as satisfied (AC-M01-12-01). Mark the org selected and route
            // into the app without calling switchOrg (there is nothing to switch).
            //
            // Validated against `realOrgMemberships`, NOT `user.memberships`:
            // the latter is built from `/iam/me`, which reports only the session's
            // own org, so on a `default`-scoped session it is EMPTY — making every
            // preference look invalid. That sent this branch's traffic to the
            // `else if` below, which treats "invalid" as "stale" and deleted the
            // user's stored preferred org on each such login.
            isPreferredOrgValid(preferredOrgId, realOrgMemberships) &&
            preferredOrgId === orgId
          ) {
            await setOrgSelected(true);
            setOrgSelectedState(true);
            if (!(await goToLanding())) {
              hasRedirected.current = false;
            }
            return;
          } else if (
            preferredOrgId &&
            !isPreferredOrgValid(preferredOrgId, realOrgMemberships)
          ) {
            // Stale preference (org removed or membership revoked) — clear it in the
            // background and show the picker. Never a hard failure.
            clearPreferredOrg(jwtPayload!.user_id).catch(() => {});
          }

          if (!(await replaceWhenLanded("/(auth)/org-select"))) {
            hasRedirected.current = false;
          }
          return;
        }

        if (!(await goToLanding())) {
          hasRedirected.current = false;
        }
      } catch {
        // FIX-04: clear the re-entry guard so the user can retry in-app after an
        // unexpected failure — prevents a silent lock-out without a page reload.
        hasRedirected.current = false;
      }
    };

    const id = setTimeout(() => {
      doRedirect();
    }, 0);

    return () => clearTimeout(id);
  }, [
    initialized,
    rootNavReady,
    isLoading,
    isPreferredOrgLoading,
    isOrganizationsLoading,
    realOrgs,
    realOrgMemberships,
    user,
    orgSelected,
    preferredOrgId,
    orgId,
    pathname,
  ]);

  /**
   * A session that ends while the app is mounted has to leave the app.
   *
   * Nothing else routes on this transition: `app/index.tsx` guards only `/`, and
   * `app/(tabs)/_layout.tsx` has no auth guard at all — so once the user is on
   * `/feeds`, flipping `isAuthenticated` to false left them sitting there. The
   * cache clear below then wiped the IAM user record, and the result was the
   * reported screen: "No name", "Unknown role", and "Access restricted" on a
   * screen they were entitled to, until they reloaded the tab by hand.
   *
   * Reloading "fixed" it because a reload re-runs `initAuth`, which re-reads the
   * cookie — the session had usually been fine all along.
   */
  useEffect(() => {
    if (isAuthenticated) {
      wasAuthenticated.current = true;
      return;
    }
    if (
      !shouldRouteToLogin({
        authenticated: isAuthenticated,
        wasAuthenticated: wasAuthenticated.current,
        // The account switcher takes the user through hosted login itself;
        // routing to /login here would cut across it.
        isSwitching,
      })
    )
      return;

    wasAuthenticated.current = false;
    queryClient.clear();
    // Drop the stale identity too, so nothing renders a half-session on the way
    // out — and so `isSessionResolved` reads a genuine signed-out state.
    setJwtPayload(null);
    setStoredRole(undefined);
    setOrgSelectedState(false);
    hasRedirected.current = false;
    router.replace("/(auth)/login");
  }, [isAuthenticated, isSwitching, queryClient]);

  /**
   * Self-heal the other way into the broken screen.
   *
   * `fetchBlocksUser` answers `null` — not an error — when the IAM read is
   * refused (401/403), so the query reports settled, not-loading, not-errored,
   * with no data. Neither the provider's spinner nor its retry screen catches
   * that, and the axios/GraphQL 401 interceptor cannot either: `/iam/me` and
   * `/iam/users/{id}` go out on a raw `fetch` from `services/blocks/session.ts`,
   * not through `apiClient`. So the app rendered on with no user record and no
   * roles and simply stayed there.
   *
   * Re-verify the session once instead. `refreshAuth` now distinguishes a real
   * 401 (→ signed out, and the effect above routes to login) from a call that
   * merely failed (→ keep the session and try again later), so this is safe to
   * fire and cannot flap. The ref keys on the identity, so it re-arms for a new
   * session but never retries in a loop for the same one.
   */
  const healedFor = useRef<string | null>(null);
  useEffect(() => {
    const userId = jwtPayload?.user_id;
    if (user) {
      healedFor.current = null; // recovered — re-arm for a future session
      return;
    }
    if (
      !userId ||
      !shouldRecheckSession({
        authenticated: isAuthenticated,
        hasIdentity: !!jwtPayload,
        hasUser: !!user,
        isUserLoading: isLoading,
        isUserError,
        alreadyTried: healedFor.current === userId,
      })
    )
      return;

    healedFor.current = userId;
    refreshAuth(true).catch(() => {});
  }, [isAuthenticated, jwtPayload?.user_id, user, isLoading, isUserError]);

  useNotificationConnection(orgId ? user : null);

  /**
   * v4 hosted SSO cannot hold two sessions at once.
   *
   * v1 kept a refresh token per account in `accountStore` and swapped the JS-held
   * pair in place. The v4 session is an HttpOnly cookie scoped to one origin, so
   * JS can neither read it nor mint a second one — an in-place switch is not
   * expressible. Switching therefore means: revoke the current session and send
   * the user through the hosted login, where they pick the other identity.
   *
   * The `accounts` list and this method are kept so the switcher UI still works
   * and so this is easy to revisit; only the mechanism changed.
   */
  const switchAccount = async (userId: string) => {
    setIsSwitching(true);
    try {
      // Remember which identity was requested so the UI can hint it after login.
      await accountStore.setActive(userId);

      await ssoLogout().catch(() => {
        // session may already be invalid — proceed to re-login regardless
      });
      clearSessionCache();
      queryClient.clear();

      setIsAuthenticated(false);
      setJwtPayload(null);
      setStoredRole(undefined);
      setOrgSelectedState(false);
      hasRedirected.current = false;

      await startLogin();
    } finally {
      setIsSwitching(false);
    }
  };

  const addAccount = () => {
    router.push("/(auth)/login?mode=add");
  };

  const logout = async () => {
    const activeId = jwtPayload?.user_id;
    if (activeId) await accountStore.remove(activeId);

    await ssoLogout().catch(() => {
      // already invalid server-side — still clear local state below
    });
    // Drops the stored "Remember me" preference and this run's marker along with
    // the tokens, so the next sign-in starts from the toggle the user sets then.
    await clearTokens();
    clearSessionCache();
    setIsAuthenticated(false);
    setJwtPayload(null);
    setStoredRole(undefined);
    hasRedirected.current = false;
    setOrgSelectedState(false);
    setAccounts(await accountStore.getAll());
    // The remembered destination belongs to the visit that just ended; the next
    // person to sign in on this tab must not inherit it.
    clearIntendedPath();
    router.replace("/(auth)/login");
  };

  const refreshAuth = async (skipRedirect: boolean = false) => {
    try {
      if (Platform.OS === "web") invalidateIdentity();
      const status = await getSessionStatus();

      // "The session is gone" and "the question could not be answered" are
      // different, and conflating them is what put the app in the broken state
      // a reload fixed: one unlucky `/iam/me` — a 500, a timeout, a moment
      // offline — used to read as signed out, and this function then cleared a
      // live session while the user sat on a mounted screen. Keep what we have
      // and let the next attempt reconcile.
      if (status === "unknown") return;

      const auth = status === "active";
      if (!auth) {
        await clearTokens();
        setIsAuthenticated(false);
        return;
      }

      // The second of the two session-establishing paths (SSO callback, org
      // switch, account switch all land here), so it reconciles too.
      const decoded = await decodeToken({ reconcileOrg: true });

      // `checkAuthenticated()` and `decodeToken()` are two independent network
      // calls — `ensureIdentity()` and `/iam/v4/iam/me` — so they can disagree.
      // When only the second fails, committing `isAuthenticated: true` alongside
      // a null payload produced a session that claimed to be signed in with no
      // identity, and it did not recover on its own:
      //
      //   - `roles` derives from `jwtPayload.roles` + the org membership, so it
      //     read empty — every permission gate slammed shut and screens rendered
      //     "Access restricted".
      //   - `appRole` was null, so the nav bar showed "Unknown role".
      //   - the IAM user query is `enabled: … && !!jwtPayload?.user_id`, so it
      //     never ran. A disabled query reports `isLoading: false`, which is why
      //     the provider's loading gate let this through instead of holding the
      //     spinner, and the nav bar showed "No name".
      //
      // A transient failure of one call must not destroy a working session: keep
      // what we have and let the next attempt reconcile.
      if (!canCommitSession(auth, decoded)) return;

      const allAccounts = await accountStore.getAll();
      const exists = allAccounts.find((a) => a.userId === decoded?.user_id);
      const accessToken = await getToken();
      const refreshToken = await getRefreshToken();
      const resolvedRefreshToken = refreshToken ?? exists?.refreshToken;

      if (decoded && accessToken && resolvedRefreshToken) {
        const role = appRoles(toRoleArray(decoded.roles))[0] ?? exists?.role;
        const organization = decoded.org_id ?? exists?.organization;

        await accountStore.upsert({
          userId: decoded.user_id,
          email: decoded.email,
          accessToken,
          refreshToken: resolvedRefreshToken,
          role,
          organization,
        });
        await accountStore.setActive(decoded.user_id);
        setStoredRole(role);
      } else if (exists?.role) {
        setStoredRole(exists.role);
      }

      queryClient.clear();

      setOrgSelectedState(isRealOrgId(decoded?.session_org_id));

      if (!skipRedirect) {
        hasRedirected.current = false;
      }

      setJwtPayload(decoded);
      setAccounts(await accountStore.getAll());
      setIsAuthenticated(auth);
    } catch {
      // Same reasoning as the `"unknown"` branch above: everything that can
      // throw in here is a network call, and a network call failing is not
      // evidence that the session ended. Ending it anyway is what stranded the
      // user on a gated screen with no identity and no roles.
    }
  };

  if (!initialized || (isAuthenticated && isLoading)) {
    return (
      <View className="flex-1 justify-center items-center bg-chalk">
        <ActivityIndicator size="large" />
      </View>
    );
  }

  // FIX-04: IAM user query failed — show an actionable error instead of leaving the
  // user on an indefinite blank screen. A retry re-runs the query; on success the
  // redirect effect fires and the normal login flow resumes (AC-M01-13-01/02).
  if (isAuthenticated && isUserError && !user) {
    return (
      <View className="flex-1 justify-center items-center bg-chalk gap-4">
        <Text className="text-center text-sm text-obsidian px-8">
          {t("ERROR_SESSION_LOAD_FAILED")}
        </Text>
        <Pressable
          onPress={() => refetchUser()}
          className="px-4 py-2 bg-primary-default rounded"
          accessibilityRole="button"
          accessibilityLabel={t("BTN_RETRY")}
        >
          <Text className="text-white text-sm">{t("BTN_RETRY")}</Text>
        </Pressable>
      </View>
    );
  }

  const value: AuthContextType = {
    user,
    userGroups,
    orgId,
    roles,
    isAdmin,
    isAuthenticated,
    isLoading,
    isSessionResolved,
    isSwitching,
    refreshAuth,
    logout,
    appRole,
    isSuperAdmin,
    accounts,
    switchAccount,
    addAccount,
    currentOrgName,
  };

  return (
    <>
      <AuthContext.Provider value={value}>
        {children}
      </AuthContext.Provider>
      {isSwitching && (
        <View className="absolute inset-0 z-[9999] bg-white/85 items-center justify-center">
          <ActivityIndicator size="large" />
        </View>
      )}
    </>
  );
};

export const useAuth = () => useContext(AuthContext)!;
